import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

// Séquence de la piste 5c (Claude Design) : fermeture du libellé, burst, puis transformation en compteur.
const CLOSE_MS = 260;
const FLY_LANDING_MS = 860;
const MORPH_START_MS = 1300;
const MORPH_MS = 420;
// Les clics rapprochés sur + / − sont regroupés en une seule requête, envoyée après ce délai.
const COMMIT_DELAY_MS = 200;
// Après un retrait complet au −, le bouton « Ajouter » revient sous le doigt : les clics en rafale
// qui suivent ne doivent pas rajouter le produit.
const ADD_GUARD_MS = 600;
const LEAVE_MS = 340;
const REOPEN_MS = 260;

class LokiAtc extends HTMLElement {
  #timers = [];
  #busy = false;
  #commitTimer = 0;
  #commitSeq = 0;
  #committedQty = 0;
  #inflight = Promise.resolve();
  #addLockedUntil = 0;
  #leaveTimer = 0;
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '{}');
    this.variantId = this.dataset.variantId;
    this.qty = Number(this.dataset.qty) || 0;
    this.#committedQty = this.qty;
    this.el = {
      label: this.querySelector('.ll-atc__label'),
      add: this.querySelector('.ll-atc__add'),
      dec: this.querySelector('.ll-atc__dec'),
      inc: this.querySelector('.ll-atc__inc'),
      digit: this.querySelector('.ll-atc__digit'),
      note: this.querySelector('.ll-atc__note'),
      sr: this.querySelector('.ll-atc__sr'),
    };
    this.el.add.addEventListener('click', () => this.add());
    this.el.inc.addEventListener('click', () => this.change(this.qty + 1));
    this.el.dec.addEventListener('click', () => this.change(this.qty - 1));

    this.form = this.closest('product-form-component')?.querySelector('form');
    this.variantInput = this.form?.querySelector('input[name="id"]');
    if (this.variantInput) {
      this.variantObserver = new MutationObserver(() => this.#onVariantChange());
      this.variantObserver.observe(this.variantInput, { attributes: true, attributeFilter: ['value'] });
      this.variantInput.addEventListener('change', () => this.#onVariantChange());
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onForeignCartUpdate);
    this.#setPhase(this.qty > 0 ? 'counter' : 'idle');
    this.#render();
  }

  disconnectedCallback() {
    if (this.#commitTimer) this.#commit();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onForeignCartUpdate);
    this.variantObserver?.disconnect();
    this.#clearTimers();
  }

  get #max() {
    return this.variants[this.variantId]?.max ?? 99;
  }

  #later(ms, fn) {
    this.#timers.push(setTimeout(fn, this.#reducedMotion.matches ? 0 : ms));
  }

  #wait(ms) {
    return new Promise((resolve) => this.#later(ms, resolve));
  }

  #clearTimers() {
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
  }

  #say(text) {
    this.el.sr.textContent = '';
    requestAnimationFrame(() => (this.el.sr.textContent = text));
  }

  #setPhase(phase) {
    this.dataset.phase = phase;
    const busy = phase === 'closing' || phase === 'burst';
    this.el.add.disabled = busy || this.hasAttribute('data-disabled');
    this.el.add.setAttribute('aria-busy', String(busy));
    const live = phase === 'counter';
    this.el.dec.tabIndex = live ? 0 : -1;
    this.el.inc.tabIndex = live ? 0 : -1;
  }

  #render() {
    this.dataset.qty = String(this.qty);
    this.el.digit.textContent = String(Math.max(this.qty, 1));
    const atMax = this.qty >= this.#max;
    this.el.inc.disabled = atMax;
    this.el.dec.setAttribute('aria-label', this.qty === 1 ? 'Retirer du panier' : 'Diminuer la quantité');
    const stockNote = atMax && this.qty > 0 ? `Plus que ${this.#max} en stock` : '';
    this.el.note.textContent = stockNote;
    this.el.note.hidden = !stockNote;
  }

  #showError(message) {
    this.el.note.textContent = message;
    this.el.note.hidden = false;
    this.#say(message);
  }

  #sectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  /**
   * Envoie la requête au panier en diffusant CartLinesUpdateEvent (pastille du header, panier latéral).
   * La promesse de l'événement est résolue au plus tôt à `notBefore` : la pastille rebondit quand la
   * vignette arrive sur l'icône, pas avant.
   */
  async #request(url, body, action, quantity, notBefore = Promise.resolve()) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: [{ merchandiseId: this.variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();
      if (data.status) throw Object.assign(new Error(data.description || data.message), { cartError: data });

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      await notBefore;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: data.sections,
          source: 'loki-atc',
          productId: this.closest('product-form-component')?.dataset.productId,
          didError: false,
        },
      });
      return cart;
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error.message || 'Cart update failed', code: 'INVALID', detail: error.cartError })
      );
      throw error;
    }
  }

  #lineQty(cart) {
    return cart.items
      .filter((item) => String(item.variant_id) === String(this.variantId))
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  #flyToCart() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.media;
    if (!cartIcon || !image || this.#reducedMotion.matches) return;
    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--main');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = this.querySelector('.ll-atc__pill');
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  async add() {
    if (this.#busy || this.dataset.phase !== 'idle' || this.hasAttribute('data-disabled')) return;
    if (performance.now() < this.#addLockedUntil) return;
    this.#busy = true;
    // Retrait à 0 pas encore envoyé : on l'envoie avant de rajouter, sinon les deux requêtes se croisent.
    if (this.#commitTimer) await this.#commit();
    this.#clearTimers();
    this.#setPhase('closing');
    this.#say('Ajout en cours');

    const landing = this.#wait(FLY_LANDING_MS);
    const request = this.#request(
      Theme.routes.cart_add_url,
      { items: [{ id: Number(this.variantId), quantity: 1 }], sections: this.#sectionIds() },
      'add',
      1,
      landing
    );
    this.#later(CLOSE_MS, () => {
      this.#setPhase('burst');
      this.#flyToCart();
    });

    try {
      const [cart] = await Promise.all([request, this.#wait(MORPH_START_MS)]);
      this.qty = Math.max(1, this.#lineQty(cart));
      this.#committedQty = this.qty;
      this.#render();
      this.#setPhase('morph');
      this.#later(MORPH_MS, () => {
        this.#setPhase('counter');
        this.#say(`${this.dataset.productTitle} ajouté au panier, quantité ${this.qty}`);
      });
    } catch (error) {
      this.#clearTimers();
      this.#setPhase('idle');
      this.#showError(error.message || 'Impossible d’ajouter ce produit pour le moment.');
    } finally {
      this.#busy = false;
    }
  }

  // Affichage immédiat, envoi groupé : le chiffre suit chaque clic, une seule requête part à la fin.
  change(next) {
    if (this.dataset.phase !== 'counter') return;
    const qty = Math.max(0, Math.min(this.#max, next));
    if (qty === this.qty) return;
    this.qty = qty;
    this.#render();

    if (qty === 0) {
      this.#addLockedUntil = performance.now() + ADD_GUARD_MS;
      this.#setPhase('leaving');
      clearTimeout(this.#leaveTimer);
      this.#leaveTimer = setTimeout(
        () => {
          if (this.dataset.phase !== 'leaving') return;
          this.#setPhase('idle');
          if (this.#reducedMotion.matches) return;
          this.dataset.reopen = '';
          setTimeout(() => delete this.dataset.reopen, REOPEN_MS);
        },
        this.#reducedMotion.matches ? 0 : LEAVE_MS
      );
      this.#say('Retiré du panier');
    } else {
      this.#say(`${qty} dans le panier`);
    }

    clearTimeout(this.#commitTimer);
    this.#commitTimer = setTimeout(() => this.#commit(), COMMIT_DELAY_MS);
  }

  async #commit() {
    clearTimeout(this.#commitTimer);
    this.#commitTimer = 0;
    const seq = ++this.#commitSeq;
    // Un seul envoi à la fois : le serveur reçoit les quantités dans l'ordre des clics.
    await this.#inflight;
    const target = this.qty;
    if (target === this.#committedQty) return;
    try {
      const request = this.#request(
        Theme.routes.cart_change_url,
        { id: String(this.variantId), quantity: target, sections: this.#sectionIds() },
        'update',
        target
      );
      this.#inflight = request.catch(() => {});
      const cart = await request;
      this.#committedQty = this.#lineQty(cart);
      // Réponse d'une requête dépassée, ou nouveaux clics depuis : l'écran a déjà la bonne valeur.
      if (seq !== this.#commitSeq || this.#commitTimer) return;
      this.#syncFromCart(cart, { silent: true });
    } catch (error) {
      if (seq !== this.#commitSeq) return;
      this.qty = this.#committedQty;
      this.#setPhase(this.qty > 0 ? 'counter' : 'idle');
      this.#render();
      this.#showError(error.message || 'Impossible de modifier la quantité.');
    }
  }

  #syncFromCart(cart, { silent = false } = {}) {
    const qty = this.#lineQty(cart);
    if (qty === this.qty && silent) return;
    this.qty = qty;
    this.#render();
    if (qty > 0 && this.dataset.phase === 'idle' && !this.#busy) this.#setPhase('counter');
    if (qty === 0 && this.dataset.phase === 'counter') this.#setPhase('idle');
  }

  // Panier modifié ailleurs (panier latéral, autre onglet de la fiche…) : on se recale sans animation.
  #onForeignCartUpdate = (event) => {
    if (event.target === this) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.items) return;
        this.#committedQty = this.#lineQty({ items: detail.items });
        if (!this.#commitTimer) this.#syncFromCart({ items: detail.items });
      })
      .catch(() => {});
  };

  async #onVariantChange() {
    const id = this.variantInput?.value;
    if (!id || id === this.variantId) return;
    this.variantId = id;
    this.dataset.variantId = id;
    this.#clearTimers();
    const available = this.variants[id]?.available !== false;
    this.toggleAttribute('data-disabled', !available);
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    this.qty = this.#lineQty(cart);
    this.#committedQty = this.qty;
    this.#setPhase(this.qty > 0 ? 'counter' : 'idle');
    this.#render();
  }
}

if (!customElements.get('loki-atc')) customElements.define('loki-atc', LokiAtc);
